/**
 * Spreadsheet.tsx - Universal Spreadsheet Component
 * 
 * CHANGE HISTORY:
 * - 251108_2031 [pending] danielbeer: feat: Update Spreadsheet to current standards with TypeScript, error handling, and documentation
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 * 
 * @module Spreadsheet
 * @description Professional spreadsheet component with full CRUD operations, undo/redo,
 * copy/paste, and integration with OpCentral ecosystem (Actr, Backtrackr, Commandr, Skinnr)
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import BaseGridView from '../BaseGridView/BaseGridView';
import { useActrQuery } from '@opcentral/actr';
import { useBacktrackr } from '@opcentral/backtrackr';
import { useCommandr } from '@opcentral/commandr';
import { useSkinnr } from '@opcentral/skinnr';
import SpreadsheetToolbar from './SpreadsheetToolbar';
import { detectColumns } from './utils/columnDetection';
import { prepareGridData } from './utils/dataPreparation';

/**
 * Column definition for spreadsheet
 */
export interface SpreadsheetColumn {
  field: string;
  headerName?: string;
  width?: number;
  flex?: number;
  sortable?: boolean;
  filter?: boolean;
  editable?: boolean;
  cellRenderer?: (params: any) => React.ReactNode;
  valueGetter?: (params: any) => any;
  valueSetter?: (params: any) => boolean;
}

/**
 * Export format options
 */
export type ExportFormat = 'csv' | 'json' | 'xlsx';

/**
 * Spreadsheet component props
 */
export interface SpreadsheetProps {
  // Data
  /** External data array to display */
  data?: any[];
  /** Dataset ID for Actr integration */
  datasetId?: string;
  /** Unique view identifier */
  viewId?: string;
  
  // Grid options
  /** Column definitions */
  columns?: SpreadsheetColumn[];
  /** Enable pagination */
  pagination?: boolean;
  /** Number of rows per page */
  pageSize?: number;
  /** Enable column filtering */
  filtering?: boolean;
  /** Enable column sorting */
  sorting?: boolean;
  /** Enable cell editing */
  editable?: boolean;
  /** Row mode - rows are single objects (no cell focus/edit). Default: true */
  rowMode?: boolean;
  /** Enable tree data mode */
  treeData?: boolean;
  
  // Export options
  /** Available export formats */
  exportOptions?: ExportFormat[];
  
  // Callbacks
  /** Callback when row is clicked */
  onRowClick?: (event: any) => void;
  /** Callback when row is double-clicked */
  onRowDoubleClick?: (event: any) => void;
  /** Callback when cell is edited */
  onCellEdit?: (params: any) => void;
  /** Callback when selection changes */
  onSelectionChange?: (rows: any[]) => void;
  
  // Styling
  /** Component height */
  height?: string;
  /** Skin ID for theming */
  skinId?: string;
  
  // Advanced
  /** Function to get data path for tree data */
  getDataPath?: (data: any) => string[];
  /** Auto group column definition for tree data */
  autoGroupColumnDef?: any;
  
  // Error handling
  /** Callback when error occurs */
  onError?: (error: Error) => void;
  /** Custom error component */
  errorComponent?: React.ComponentType<{ error: Error }>;
  /** Custom loading component */
  loadingComponent?: React.ComponentType;
  /** Custom empty state component */
  emptyComponent?: React.ComponentType;
}

/**
 * Universal Spreadsheet Component
 * 
 * @description Professional spreadsheet with full CRUD, undo/redo, copy/paste,
 * and integration with OpCentral packages
 * 
 * @example
 * ```tsx
 * <Spreadsheet
 *   data={myData}
 *   columns={[
 *     { field: 'name', headerName: 'Name', sortable: true },
 *     { field: 'email', headerName: 'Email', filter: true }
 *   ]}
 *   pagination={true}
 *   pageSize={50}
 *   onRowClick={(event) => console.log(event.data)}
 * />
 * ```
 * 
 * @example With Actr dataset
 * ```tsx
 * <Spreadsheet
 *   datasetId="contacts"
 *   viewId="contacts-spreadsheet"
 *   editable={true}
 *   onCellEdit={(params) => console.log('Cell edited:', params)}
 * />
 * ```
 */
export const Spreadsheet: React.FC<SpreadsheetProps> = ({
  // Data
  data: externalData,
  datasetId,
  viewId = 'spreadsheet-view',
  
  // Grid options
  columns: customColumns,
  pagination = true,
  pageSize = 50,
  filtering = true,
  sorting = true,
  editable = false,
  rowMode = true, // Default: rows are single objects
  treeData = false,
  
  // Export options
  exportOptions = ['csv', 'json', 'xlsx'],
  
  // Callbacks
  onRowClick,
  onRowDoubleClick,
  onCellEdit,
  onSelectionChange,
  
  // Styling
  height = '600px',
  skinId,
  
  // Advanced
  getDataPath,
  autoGroupColumnDef,
  
  // Error handling
  onError,
  errorComponent: ErrorComponent,
  loadingComponent: LoadingComponent,
  emptyComponent: EmptyComponent,
  
  ...otherProps
}) => {
  const gridRef = useRef<any>(null);
  const [gridApi, setGridApi] = useState<any>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Use dataset if provided, otherwise use external data
  const actrQuery = datasetId ? useActrQuery(datasetId) : null;
  const data = externalData || actrQuery?.data || [];
  
  // Backtrackr for undo/redo
  const backtrackrContext = useBacktrackr();
  const { recordChange } = backtrackrContext || {};
  
  // Commandr for copy/paste
  const commandrContext = useCommandr();
  const { copy, paste } = commandrContext || {};
  
  // Skinnr for theming
  const skinnrContext = useSkinnr();
  const { getCurrentMode } = skinnrContext || {};
  
  /**
   * Handle errors with optional callback
   */
  const handleError = useCallback((err: Error) => {
    console.error('[Spreadsheet] Error:', err);
    setError(err);
    if (onError) {
      onError(err);
    }
  }, [onError]);
  
  /**
   * Auto-detect columns if not provided
   */
  const columns = useMemo(() => {
    try {
      if (customColumns) return customColumns;
      if (data.length === 0) return [];
      return detectColumns(data);
    } catch (err) {
      handleError(err as Error);
      return [];
    }
  }, [customColumns, data, handleError]);
  
  /**
   * Prepare data for grid
   */
  const gridData = useMemo(() => {
    try {
      return prepareGridData(data, { treeData, getDataPath });
    } catch (err) {
      handleError(err as Error);
      return [];
    }
  }, [data, treeData, getDataPath, handleError]);
  
  /**
   * Determine skin based on mode (unless explicitly provided)
   */
  const effectiveSkinId = useMemo(() => {
    try {
      if (skinId) return skinId;
      if (!getCurrentMode) return 'grid-light';
      const mode = getCurrentMode();
      return mode === 'dark' ? 'grid-dark' : 'grid-light';
    } catch (err) {
      handleError(err as Error);
      return 'grid-light';
    }
  }, [skinId, getCurrentMode, handleError]);
  
  /**
   * Handle cell edit with undo/redo tracking
   */
  const handleCellEdit = useCallback((params: any) => {
    try {
      const { data: rowData, colDef } = params;
      
      // Record change for undo/redo
      if (recordChange) {
        recordChange({
          type: 'update',
          itemId: rowData.id,
          field: colDef.field,
          oldValue: params.oldValue,
          newValue: params.newValue,
        });
      }
      
      // Update item in dataset
      if (actrQuery?.updateItem && rowData.id) {
        actrQuery.updateItem(rowData.id, rowData);
      }
      
      // Call custom callback
      if (onCellEdit) {
        onCellEdit(params);
      }
    } catch (err) {
      handleError(err as Error);
    }
  }, [recordChange, actrQuery, onCellEdit, handleError]);
  
  /**
   * Handle grid ready event
   */
  const handleGridReady = useCallback((params: any) => {
    try {
      setGridApi(params.api);
      gridRef.current = params.api;
    } catch (err) {
      handleError(err as Error);
    }
  }, [handleError]);
  
  /**
   * Keyboard shortcuts for copy/paste/delete
   */
  useEffect(() => {
    if (!gridApi) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      try {
        // Copy (Cmd/Ctrl + C)
        if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
          if (copy) {
            const selectedRows = gridApi.getSelectedRows();
            if (selectedRows.length > 0) {
              copy(selectedRows);
              e.preventDefault();
            }
          }
        }
        
        // Paste (Cmd/Ctrl + V)
        if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
          if (paste) {
            paste();
            e.preventDefault();
          }
        }
        
        // Delete (Delete/Backspace)
        if (e.key === 'Delete' || e.key === 'Backspace') {
          const selectedRows = gridApi.getSelectedRows();
          if (selectedRows.length > 0 && actrQuery?.deleteItem) {
            selectedRows.forEach((row: any) => {
              if (recordChange) {
                recordChange({
                  type: 'delete',
                  itemId: row.id,
                  data: row,
                });
              }
              actrQuery.deleteItem(row.id);
            });
          }
        }
      } catch (err) {
        handleError(err as Error);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [gridApi, copy, paste, actrQuery, recordChange, handleError]);
  
  // Error state
  if (error) {
    if (ErrorComponent) {
      return <ErrorComponent error={error} />;
    }
    return (
      <div className="spreadsheet-error" style={{ padding: '2rem', textAlign: 'center' }}>
        <h3>⚠️ Error Loading Spreadsheet</h3>
        <p>{error.message}</p>
        <button onClick={() => setError(null)}>Try Again</button>
      </div>
    );
  }
  
  // Loading state
  if (isLoading) {
    if (LoadingComponent) {
      return <LoadingComponent />;
    }
    return (
      <div className="spreadsheet-loading" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading spreadsheet...</p>
      </div>
    );
  }
  
  // Empty state
  if (data.length === 0 && !datasetId) {
    if (EmptyComponent) {
      return <EmptyComponent />;
    }
    return (
      <div className="spreadsheet-empty" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No data to display</p>
      </div>
    );
  }
  
  return (
    <div 
      className="spreadsheet-view" 
      style={{ 
        height, 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '12px' 
      }}
    >
      {/* Toolbar */}
      <SpreadsheetToolbar
        gridRef={gridRef}
        gridApi={gridApi}
        data={gridData}
        columns={columns}
        exportOptions={exportOptions}
        viewId={viewId}
      />
      
      {/* Grid */}
      <BaseGridView
        data={gridData}
        columns={columns}
        pagination={pagination}
        pageSize={pageSize}
        filtering={filtering}
        sorting={sorting}
        editable={editable}
        rowMode={rowMode}
        skinId={effectiveSkinId}
        height="100%"
        onGridReady={handleGridReady}
        onCellEdit={handleCellEdit}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        onSelectionChange={onSelectionChange}
        {...otherProps}
      />
    </div>
  );
};

export default Spreadsheet;
